<template>
  <AppStats :items="stats" :featured="true" />
</template>

<script lang="ts" setup>
import { computed, ref, Ref, watch } from 'vue'
import AppStats, { StatItem } from '@/components/app/AppStats.vue'
import { useQuery } from '@tanstack/vue-query'
import { eq, orderByDesc, dateRangeConstants, dateRangeFilter } from '@/utils/queryFilters'
import InvoiceService from '@/services/InvoiceService'
import { INVOICE_STATUS } from '@/models/invoice'
import { BILL_STATUS } from '@/models/bill'
import { currency } from '@/utils'
import BillService from '@/services/BillService'

const props = defineProps({
  statsDateRange: { 
    type: String,
    required: true,
    default: dateRangeConstants.this_month
  }
})

const statsDateRange = ref(props.statsDateRange)

watch(() => props.statsDateRange, (newVal) => {
  statsDateRange.value = newVal
})

const { data: invoices } = useQuery({
  queryKey: ['invoices', statsDateRange],
  queryFn: async () => await new InvoiceService().where(
      [dateRangeFilter('invoiceDate', statsDateRange.value)],
      [orderByDesc('invoiceDate')]
    )
})

const { data: bills } = useQuery({
  queryKey: ['bills', 'expenses', statsDateRange],
  queryFn: async () => await new BillService().where(
      [eq('status', BILL_STATUS.PAID), dateRangeFilter('billDate', statsDateRange.value)],
      [orderByDesc('billDate')]
    )
})

// Total expenses; sum of all bills with status 'paid'
const totalExpenses = computed(() => {
  if (!bills.value) return '--'
  const paidBills = bills.value?.filter(bill => bill.status === BILL_STATUS.PAID)
  const total = paidBills.reduce((acc, bill) => acc + bill.total, 0)
  return total == 0 ? '--' : currency(total)
})

// Total revenue; sum of all invoices with status 'paid'
const totalRevenue = computed(() => {
  if (!invoices.value) return '--'
  const paidInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.PAID)
  const total = paidInvoices.reduce((acc, invoice) => acc + invoice.total, 0)
  return total == 0 ? '--' : currency(total)
})

// Total outstanding invoices
const totalOutstandingInvoices = computed(() => {
  if (!invoices.value) return '--'
  const outstandingInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.OUTSTANDING)
  const total = outstandingInvoices.reduce((acc, invoice) => acc + invoice.balance, 0)
  return total == 0 ? '--' : currency(total)
})

// Total overdue invoices
const totalOverdueInvoices = computed(() => {
  if (!invoices.value) return '--'
  const overdueInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.OVERDUE)
  const total = overdueInvoices.reduce((acc, invoice) => acc + invoice.balance, 0)
  return total == 0 ? '--' : currency(total)
})

const stats: Ref<StatItem[]> = ref([
  { label: 'totalRevenue', name: 'Total Revenue', value: totalRevenue },
  { label: 'overdueInvoices', name: 'Overdue invoices', value: totalOverdueInvoices },
  { label: 'outstandingInvoices', name: 'Outstanding invoices', value: totalOutstandingInvoices },
  { label: 'expenses', name: 'Expenses', value: totalExpenses },
])
</script>